/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { TableColumnsType } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PlatformContextlake } from 'account-client'
import { useTranslateEnum } from '@/hooks/useTranslateEnum'
import { LayoutTableColumn } from '@/layouts/LayoutTableColumn'
import { CloudLabelWithIcon } from '@/pages/cloud/features/CloudLabelWithIcon'
import { ContextlakeEdition } from '@/pages/contextlake/interface'
import { timeFormatter } from '@/utils/timeFormatter'

export function useContractContextlakesColumns() {
  const { t } = useTranslation()
  const { translate } = useTranslateEnum()

  const columns = useMemo<TableColumnsType<PlatformContextlake>>(
    () => [
      {
        title: t('Context lake Name / ID'),
        dataIndex: 'name',
        render: (value, record) => {
          // NOTE:
          // There is a rename job that may replace "contextlakes" with "contextlake" in code.
          // For backward compatibility, old instances whose id starts with "dc" must still use the
          // original "contextlakes" path segment. New instances should use "contextlake".
          // We intentionally build "contextlakes" as 'data' + 'clouds' to avoid being rewritten by the job.
          const contextlakePathSegment = record.id.startsWith('dc') ? 'data' + 'clouds' : 'contextlakes'

          return (
            <LayoutTableColumn.BilayerSpace subtitle={record.id} tipText={t('ID')}>
              <a href={`${record.regionURL}/${contextlakePathSegment}/${record.id}?from_center=true`} target="_blank">
                {value}
              </a>
            </LayoutTableColumn.BilayerSpace>
          )
        },
      },
      {
        title: t('Edition'),
        dataIndex: 'edition',
        render: value => translate(value as ContextlakeEdition),
      },
      {
        title: t('Cloud Provider'),
        dataIndex: 'cloud',
        render: value => <CloudLabelWithIcon cloud={value} />,
      },
      {
        title: t('Region'),
        dataIndex: 'region',
      },
      {
        title: t('Creator'),
        dataIndex: 'creator',
      },
      {
        title: t('Created at'),
        dataIndex: 'gmtCreated',
        sorter: true,
        render: date => timeFormatter.format(date),
      },
    ],
    [t, translate],
  )

  return columns
}
